"use client";

import {
  type ReactNode,
  useState,
  useEffect,
  useMemo,
  useRef,
  type FC,
  type Ref,
} from "react";
import { motion, AnimatePresence } from "motion/react";
import { isWindowDefined, mergeClasses } from "@huub-web/utils";

import { transitionDurations } from "../../theme";
import type { ClassNameProp } from "../../props";

import {
  slidesContainerStyle,
  slideStyle,
  slideContentStyle,
} from "./Slides.css";
import type { Timeout } from "../../types";

const NAVIGATION_DEBOUNCE_MS = 400;

export type SlideDefinition =
  | ReactNode
  | {
      slug?: string;
      component: ReactNode;
    };

export type SlidesProps = ClassNameProp & {
  /**
   * Object containing slides with slideKey as key and content as value
   * Each slide can be a ReactNode (for backward compatibility) or an object with optional slug and component
   */
  slides: Record<string, SlideDefinition>;
  /**
   * Initial slide key to display
   */
  initialSlide?: string;
  /**
   * Callback when slide changes
   */
  onSlideChange?: (slideKey: string, direction: "next" | "previous") => void;
  /**
   * Animation duration in seconds
   */
  duration?: number;
  /**
   * Enable URL hash synchronization for navigation
   * When enabled, the current slide key will be reflected in the URL hash
   * and browser back/forward navigation will work
   */
  syncWithHash?: boolean;
  /**
   * Variant of the slides
   */
  variant?: "slide" | "fade";
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<SlidesRef>;
};

export type SlidesRef = {
  /**
   * Navigate to next slide
   */
  next: () => void;
  /**
   * Navigate to previous slide
   */
  previous: () => void;
  /**
   * Navigate to specific slide
   */
  goTo: (slideKey: string) => void;
  /**
   * Get current slide key
   */
  getCurrentSlide: () => string;
};

/**
 * Helper to normalize slide definition to extract slug and component
 */
const normalizeSlideDefinition = (
  slideKey: string,
  definition: SlideDefinition,
): { slug: string; component: ReactNode } => {
  // Handle ReactNode directly (backward compatibility)
  if (
    !definition ||
    typeof definition !== "object" ||
    !("component" in definition)
  ) {
    return { slug: slideKey, component: definition };
  }

  // Handle object with optional slug
  return {
    slug: definition.slug || slideKey,
    component: definition.component,
  };
};

/**
 * Slides component that displays content in a stack with smooth transitions.
 * Uses Stack for positioning and Framer Motion for animations.
 */
export const Slides: FC<SlidesProps> = ({
  slides,
  initialSlide,
  onSlideChange,
  duration = transitionDurations.m,
  variant = "slide",
  syncWithHash = false,
  className,
  ref,
  ...props
}) => {
  const slideKeys = Object.keys(slides);
  const slideContentRef = useRef<HTMLDivElement>(null);
  const navigationTimeoutRef = useRef<Timeout | null>(null);
  const [height, setHeight] = useState<number | "auto">("auto");
  const [isNavigationLocked, setIsNavigationLocked] = useState(false);

  /**
   * Locks navigation for a short duration to prevent rapid clicks
   */
  const lockNavigation = () => {
    setIsNavigationLocked(true);

    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    navigationTimeoutRef.current = setTimeout(() => {
      setIsNavigationLocked(false);
    }, NAVIGATION_DEBOUNCE_MS);
  };

  // Build mapping between keys and slugs
  const [keyToSlug, slugToKey] = useMemo(() => {
    const kts: Record<string, string> = {};
    const stk: Record<string, string> = {};
    slideKeys.forEach((key) => {
      const { slug } = normalizeSlideDefinition(key, slides[key]);
      kts[key] = slug;
      stk[slug] = key;
    });
    return [kts, stk] as const;
  }, [slides]);

  // Always use the same initial state for both server and client (SSR-safe)
  const firstSlideKey = initialSlide || slideKeys[0];

  const [currentSlideKey, setCurrentSlideKey] = useState<string>(firstSlideKey);
  const [direction, setDirection] = useState<"next" | "previous">("next");
  const [isInitial, setIsInitial] = useState(true);

  // Load hash-based slide AFTER hydration (client-side only)
  useEffect(() => {
    if (!syncWithHash || !isWindowDefined()) {
      return;
    }

    const hash = window.location.hash.slice(1);
    const targetKey = slugToKey[hash];

    if (hash && targetKey && targetKey !== currentSlideKey) {
      const currentIndex = slideKeys.indexOf(currentSlideKey);
      const targetIndex = slideKeys.indexOf(targetKey);
      const newDirection = targetIndex > currentIndex ? "next" : "previous";

      setCurrentSlideKey(targetKey);
      setIsInitial(false);
      handleSlideChange?.(targetKey, newDirection);
    }
  }, []);

  // Update height when slide content changes
  useEffect(() => {
    if (!slideContentRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setHeight(entry.contentRect.height); // TODO: debounce this?
      }
    });

    resizeObserver.observe(slideContentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [currentSlideKey]);

  // Cleanup navigation timeout on unmount
  useEffect(
    () => () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    },
    [],
  );

  // Navigation functions
  const next = () => {
    if (isNavigationLocked) return;

    const currentIndex = slideKeys.indexOf(currentSlideKey);
    const nextIndex = (currentIndex + 1) % slideKeys.length;
    const nextSlideKey = slideKeys[nextIndex];

    lockNavigation();
    setIsInitial(false);
    setDirection("next");
    setCurrentSlideKey(nextSlideKey);
    handleSlideChange?.(nextSlideKey, "next");
  };

  const previous = () => {
    if (isNavigationLocked) return;

    const currentIndex = slideKeys.indexOf(currentSlideKey);
    const prevIndex =
      currentIndex === 0 ? slideKeys.length - 1 : currentIndex - 1;
    const prevSlideKey = slideKeys[prevIndex];

    lockNavigation();
    setIsInitial(false);
    setDirection("previous");
    setCurrentSlideKey(prevSlideKey);
    handleSlideChange?.(prevSlideKey, "previous");
  };

  const goTo = (slideKey: string) => {
    if (isNavigationLocked) return;
    if (!slideKeys.includes(slideKey)) return;
    if (slideKey === currentSlideKey) return;

    const currentIndex = slideKeys.indexOf(currentSlideKey);
    const targetIndex = slideKeys.indexOf(slideKey);
    const newDirection = targetIndex > currentIndex ? "next" : "previous";

    lockNavigation();
    setIsInitial(false);
    setDirection(newDirection);
    setCurrentSlideKey(slideKey);
    handleSlideChange?.(slideKey, newDirection);
  };

  const handleSlideChange = (
    slideKey: string,
    direction: "next" | "previous",
  ) => {
    onSlideChange?.(slideKey, direction);
  };

  const getCurrentSlide = () => currentSlideKey;

  // Expose methods via ref
  if (ref) {
    if (typeof ref === "function") {
      ref({ next, previous, goTo, getCurrentSlide });
    } else {
      ref.current = { next, previous, goTo, getCurrentSlide };
    }
  }

  // Sync current slide to URL hash
  useEffect(() => {
    if (!syncWithHash || !isWindowDefined()) {
      return;
    }

    const currentSlug = keyToSlug[currentSlideKey];
    const currentHash = window.location.hash.slice(1);
    if (currentSlug && currentHash !== currentSlug) {
      window.history.replaceState(null, "", `#${currentSlug}`);
    }
  }, [currentSlideKey, syncWithHash, keyToSlug]);

  // Listen to hash changes for browser back/forward navigation
  useEffect(() => {
    if (!syncWithHash || !isWindowDefined()) {
      return;
    }

    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      const targetKey = slugToKey[hash];

      if (hash && targetKey && targetKey !== currentSlideKey) {
        const currentIndex = slideKeys.indexOf(currentSlideKey);
        const targetIndex = slideKeys.indexOf(targetKey);
        const newDirection = targetIndex > currentIndex ? "next" : "previous";

        setIsInitial(false);
        setDirection(newDirection);
        setCurrentSlideKey(targetKey);

        // Call onSlideChange to notify parent (e.g., wizard)
        onSlideChange?.(targetKey, newDirection);
      }
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [currentSlideKey, slideKeys, syncWithHash, onSlideChange, slugToKey]);

  // Animation variants
  const slideVariants =
    variant === "slide"
      ? {
          enter: (direction: "next" | "previous") => ({
            x: direction === "next" ? "100%" : "-100%",
            opacity: 0,
          }),
          center: {
            x: 0,
            opacity: 1,
          },
          exit: (direction: "next" | "previous") => ({
            x: direction === "next" ? "-100%" : "100%",
            opacity: 0,
          }),
        }
      : {
          enter: {
            opacity: 0,
          },
          center: {
            opacity: 1,
          },
          exit: {
            opacity: 0,
          },
        };

  const currentSlideContent = normalizeSlideDefinition(
    currentSlideKey,
    slides[currentSlideKey],
  ).component;

  return (
    <motion.div
      animate={{ height }}
      transition={{ duration, ease: "easeInOut" }}
      className={mergeClasses(slidesContainerStyle, className)}
      {...props}
    >
      <AnimatePresence mode="sync" custom={direction}>
        <motion.div
          key={currentSlideKey}
          className={slideStyle}
          custom={direction}
          variants={slideVariants}
          initial={isInitial ? "center" : "enter"}
          animate="center"
          exit="exit"
          transition={{ duration, ease: "easeInOut" }}
        >
          <div ref={slideContentRef} className={slideContentStyle}>
            {currentSlideContent}
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};
